export interface HighlightContentOptions {
  linkClassName?: string
  mentionClassName?: string
  hashtagClassName?: string
}

const STORED_MENTION_RE = /@\[([^\]]+)\]\((?:fb:\d+|urn:li:(?:person|organization):[^)]+)\)/g

/**
 * Strips stored mention markup (@[Name](id)) to plain display text (@Name)
 * and returns the extracted mention names for precise highlighting.
 */
export const stripMentionMarkup = (text: string): { stripped: string; mentionNames: string[] } => {
  const mentionNames: string[] = []
  const stripped = text.replace(STORED_MENTION_RE, (_, name: string) => {
    mentionNames.push(name)
    return `@${name}`
  })
  return { stripped, mentionNames }
}

export const highlightContent = (
  text: string,
  { linkClassName, mentionClassName, hashtagClassName }: HighlightContentOptions,
) => {
  if (!text) return ''
  const linkRegex = /(https?:\/\/[^\s]+)/g
  const hashtagRegex = /(#\w+)/g

  const { stripped, mentionNames } = stripMentionMarkup(text)
  let highlightedText = stripped

  if (linkClassName) {
    highlightedText = highlightedText.replace(linkRegex, (match) => `<span class="${linkClassName}">${match}</span>`)
  }

  if (mentionClassName) {
    // First, highlight multi-word mention names extracted from stored markup
    // Replace longest names first to avoid partial matches
    const sorted = [...mentionNames].sort((a, b) => b.length - a.length)
    for (const name of sorted) {
      highlightedText = highlightedText.replaceAll(
        `@${name}`,
        `<span class="${mentionClassName}">@${name}</span>`,
      )
    }
    // Then highlight remaining plain @mentions not already wrapped
    highlightedText = highlightedText.replace(/(?<!">)(@[\w.]+)/g, (match) => {
      return `<span class="${mentionClassName}">${match}</span>`
    })
  }

  if (hashtagClassName) {
    highlightedText = highlightedText.replace(
      hashtagRegex,
      (match) => `<span class="${hashtagClassName}">${match}</span>`,
    )
  }

  return highlightedText
}
